import React, { useState } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';

import Select from '../../../components/ui/Select';

const HabitatComparisonTool = ({ zones }) => {
  const [leftZone, setLeftZone] = useState(zones?.[0]?.id || '');
  const [rightZone, setRightZone] = useState(zones?.[1]?.id || '');
  const [comparisonMetric, setComparisonMetric] = useState('biodiversity');

  const zoneOptions = zones?.map(zone => ({
    value: zone?.id,
    label: zone?.name
  }));

  const metricOptions = [
    { value: 'biodiversity', label: 'Biodiversity Index' },
    { value: 'conservation', label: 'Conservation Status' },
    { value: 'accessibility', label: 'Visitor Accessibility' },
    { value: 'threats', label: 'Threat Level' }
  ];

  const leftZoneData = zones?.find(z => z?.id === leftZone);
  const rightZoneData = zones?.find(z => z?.id === rightZone);

  const getMetricValue = (zone, metric) => {
    switch (metric) {
      case 'biodiversity':
        return zone?.biodiversityIndex;
      case 'conservation':
        return zone?.conservationScore;
      case 'accessibility':
        return zone?.accessibilityScore;
      case 'threats':
        return zone?.threatLevel;
      default:
        return 0;
    }
  };

  const getMetricColor = (value, metric) => {
    if (metric === 'threats') {
      return value > 70 ? 'text-error' : value > 40 ? 'text-warning' : 'text-success';
    }
    return value > 70 ? 'text-success' : value > 40 ? 'text-warning' : 'text-error';
  };

  return (
    <div className="bg-card rounded-lg p-6 shadow-conservation">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-semibold text-foreground">Habitat Comparison Tool</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Compare ecological zones side by side
          </p>
        </div>
        <Icon name="GitCompare" size={24} color="var(--color-primary)" />
      </div>
      {/* Metric Selection */}
      <div className="mb-6">
        <Select
          label="Comparison Metric"
          options={metricOptions}
          value={comparisonMetric}
          onChange={setComparisonMetric}
        />
      </div>
      {/* Comparison Grid */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Left Zone */}
        <div className="space-y-4">
          <Select
            label="Select Zone"
            options={zoneOptions}
            value={leftZone}
            onChange={setLeftZone}
          />
          
          {leftZoneData && (
            <div className="space-y-4">
              <div className="relative h-48 rounded-lg overflow-hidden">
                <Image
                  src={leftZoneData?.image}
                  alt={leftZoneData?.imageAlt}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 gradient-conservation opacity-40"></div>
              </div>

              <div className="bg-muted p-4 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">
                    {metricOptions?.find(m => m?.value === comparisonMetric)?.label}
                  </span>
                  <span className={`text-2xl font-bold ${getMetricColor(getMetricValue(leftZoneData, comparisonMetric), comparisonMetric)}`}>
                    {getMetricValue(leftZoneData, comparisonMetric)}%
                  </span>
                </div>
                <div className="w-full bg-background rounded-full h-2 overflow-hidden">
                  <div
                    className={`h-full ${
                      comparisonMetric === 'threats'
                        ? getMetricValue(leftZoneData, comparisonMetric) > 70 ? 'bg-error': 'bg-warning' :'bg-success'
                    }`}
                    style={{ width: `${getMetricValue(leftZoneData, comparisonMetric)}%` }}
                  ></div>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Species Count</span>
                  <span className="font-semibold text-foreground">{leftZoneData?.speciesCount}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Area</span>
                  <span className="font-semibold text-foreground">{leftZoneData?.area}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Elevation Range</span>
                  <span className="font-semibold text-foreground">{leftZoneData?.elevationRange}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right Zone */}
        <div className="space-y-4">
          <Select
            label="Select Zone"
            options={zoneOptions}
            value={rightZone}
            onChange={setRightZone}
          />
          
          {rightZoneData && (
            <div className="space-y-4">
              <div className="relative h-48 rounded-lg overflow-hidden">
                <Image
                  src={rightZoneData?.image}
                  alt={rightZoneData?.imageAlt}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 gradient-conservation opacity-40"></div>
              </div>

              <div className="bg-muted p-4 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">
                    {metricOptions?.find(m => m?.value === comparisonMetric)?.label}
                  </span>
                  <span className={`text-2xl font-bold ${getMetricColor(getMetricValue(rightZoneData, comparisonMetric), comparisonMetric)}`}>
                    {getMetricValue(rightZoneData, comparisonMetric)}%
                  </span>
                </div>
                <div className="w-full bg-background rounded-full h-2 overflow-hidden">
                  <div
                    className={`h-full ${
                      comparisonMetric === 'threats'
                        ? getMetricValue(rightZoneData, comparisonMetric) > 70 ? 'bg-error': 'bg-warning' :'bg-success'
                    }`}
                    style={{ width: `${getMetricValue(rightZoneData, comparisonMetric)}%` }}
                  ></div>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Species Count</span>
                  <span className="font-semibold text-foreground">{rightZoneData?.speciesCount}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Area</span>
                  <span className="font-semibold text-foreground">{rightZoneData?.area}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Elevation Range</span>
                  <span className="font-semibold text-foreground">{rightZoneData?.elevationRange}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Comparison Summary */}
      {leftZoneData && rightZoneData && (
        <div className="mt-6 p-4 bg-muted rounded-lg">
          <h4 className="text-sm font-semibold text-foreground mb-2">Comparison Insights</h4>
          <p className="text-sm text-muted-foreground">
            {leftZoneData?.name} has {getMetricValue(leftZoneData, comparisonMetric) > getMetricValue(rightZoneData, comparisonMetric) ? 'higher' : 'lower'} {metricOptions?.find(m => m?.value === comparisonMetric)?.label?.toLowerCase()} compared to {rightZoneData?.name}. 
            {comparisonMetric === 'biodiversity' && ' This indicates different levels of species richness and ecosystem complexity.'}
            {comparisonMetric === 'conservation' && ' This reflects varying conservation management effectiveness and habitat protection status.'}
            {comparisonMetric === 'accessibility' && ' This affects visitor experience and tourism potential in each zone.'}
            {comparisonMetric === 'threats' && ' This highlights areas requiring immediate conservation attention and intervention.'}
          </p>
        </div>
      )}
    </div>
  );
};

export default HabitatComparisonTool;